import { createContext } from "react"

import exports from '../awsExports'
import { useAuthenticator } from '@aws-amplify/ui-react'

import {
  inviteUser,
  updateUser,
  reinviteUser as reinviteUserQuery,
  cancelUserInvite as cancelUserInviteQuery,
} from '../graphql/mutations'


import { generateClient } from '@aws-amplify/api'

// Cannot use the auto-generated validateInvite query due to permission issues
const validateInviteQuery = /* GraphQL */ `
  query ValidateInvite($inviteId: ID) {
    validateInvite(inviteId: $inviteId) {
      inviteId
      inviteStatus
      inviteExpiration
      emailAddress
      userId
      customer {
        customerId
        customerName
      }
    }
  }
`;

// Hand-maintained query — intentionally NOT the auto-generated `listUsers` from
// ../graphql/queries. `amplify codegen` (yarn generateSchema) overwrites that file
// and re-adds a deep `customer { ... vows { ... } }` block (.graphqlconfig.yml
// maxDepth: 20). That makes AppSync resolve User.customer (getCustomerResolver)
// and Customer.vows (listVowConfigs) once PER user row — an N+1 the User
// Management list never uses (it only renders name/role/email/status). A prior
// trim of the generated file (#77) was silently regenerated back by codegen (#94),
// so the lean query must live here to survive schema refreshes.
const listUsersQuery = /* GraphQL */ `
  query ListUsers {
    listUsers {
      userId
      customerId
      emailAddress
      status
      role
      allowEditing
      fullName
      firstName
      lastName
    }
  }
`;

const UserContext = createContext()
export default UserContext

const ContextState = () => {
  const { user, signOut } = useAuthenticator((context) => [context.user])

  // Migrating from Amplify V5 to V6.
  // https://docs.amplify.aws/react/migrate-v5-to-v6/migrate-graphql-api/
  const client = generateClient();

  const listAllUsers = async () => {
    const result = await client.graphql({
      query: listUsersQuery,
      authMode: 'userPool',
    })

    return result.data.listUsers
  }

  const invite = async ({ email, role }) => {
    const result = await client.graphql({
      query: inviteUser,
      authMode: 'userPool',
      variables: {
        input: {
          emailAddress: email,
          role,
        },
      },
    })

    return result.data.inviteUser
  }

  const reinviteUser = async (userId) => {
    const result = await client.graphql({
      query: reinviteUserQuery,
      authMode: 'userPool',
      variables: {
        input: {
          userId,
        },
      },
    })

    return result.data.reinviteUser
  }

  

  const cancelUserInvite = async (userId) => {
    const result = await client.graphql({
      query: cancelUserInviteQuery,
      authMode: 'userPool',
      variables: {
        input: {
          userId,
        },
      },
    })

    return result.data.cancelUserInvite
  }

  const updateCurrentUser = async (userId, { role, status }) => {
    const result = await client.graphql({
      query: updateUser,
      authMode: 'userPool',
      variables: {
        input: {
          userId,
          role,
          status,
        },
      },
    })

    return result.data.updateUser
  
  }

  // This function uses Lambda auth against the validateInvite function in mesh-launchpad-backend.
  // IMPORTANT: The Amplify v5 `authMode: AWS_LAMBDA` would fall back to an empty (unauthenticated)
  // auth token. With Amplify v6 this is no longer permitted. The code is updated to pass the inviteId
  // as the authToken for now. We can improve backend handling later.
  const validateInvite = async ({ inviteId }) => {
    const result = await client.graphql({
      query: validateInviteQuery,
      authMode: 'lambda',
      // Added as a dummy authToken to satisfy Amplify v6. We should probably handle this better in the backend.
      authToken: inviteId,
      variables: {
        inviteId,
      },
    })

    const { emailAddress, userId, inviteStatus, customer } = result.data.validateInvite
    return {
      inviteId,
      emailAddress,
      userId,
      inviteStatus,
      ...customer,
    }
  }

  return {
    auth: {
      user,
      signOut,
    },
    validateInvite,
    listAllUsers,
    invite,
    reinviteUser,
    cancelUserInvite,
    updateCurrentUser,
  }
}

const UserContextProvider = (props) => {
  const state = ContextState()

  return (
    <UserContext.Provider value={state}>
      {props.children}
    </UserContext.Provider>
  ) 
}

export { UserContext, UserContextProvider }
